import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { Chip } from '../Chip';

export interface TimelineItem {
  label: string;
  datetime?: string;
  variant: 'occurred' | 'estimated';
  dotColor?: 'default' | 'warning';
  badge?: string;
  // A short statement about this row, rendered under the datetime/badge and above `action` as a
  // NEUTRAL CHIP WITH A SMALL BOLD LABEL — the facade owns that presentation, the caller only
  // supplies the words. Compact on purpose: the chip states a fact ABOUT the row, so it sits under
  // the row's own text without competing with the label above it.
  // Deliberately a `string` and NOT a `ReactNode` like `action`: `action` is a caller-owned
  // control, whereas this is text the facade renders itself, so keeping it a string makes
  // smuggling markup into the trazabilidad UNREPRESENTABLE instead of merely discouraged.
  // The chip's default colour carries the ink at `text.primary` (#50495E) on an 8% tint of it
  // (#F1F0F2) — **7.53:1**, well over WCAG 1.4.3's 4.5:1 for normal text. The brand
  // `text.secondary` (#ACA5BB) would have measured 2.37:1 and was rejected for this reason: the
  // first consumer of this slot is the pickup authorship, a line that decides a compensation, so it
  // is the wrong place to economize contrast. One colour for every value the caller passes, so
  // nothing about the meaning rests on the fill (WCAG 1.4.1) — the words carry it.
  note?: string;
  // A caller-owned control hung off this row, rendered below the datetime/badge and inside the
  // row's content column so it lines up with the label rather than the marker rail. The wrapper
  // composes nothing and knows nothing about what it is handed — that is the point of the slot:
  // it lets a slice put an action NEXT TO THE DATUM IT CHANGES («Ampliar días» on the expiry
  // milestone) without the facade learning a single domain concept.
  action?: ReactNode;
}

export interface TimelineProps {
  items: TimelineItem[];
}

// Intrinsic marker glyph size (px), not layout spacing — a fixed dot, so it stays a
// named constant rather than a spacing-scale unit.
const DOT_SIZE = 12;

// One trazabilidad row. The marker (dot + connector) is decorative and aria-hidden —
// the meaning lives in the label text — so the occurred/estimated distinction never
// rests on colour: `occurred` = solid dot + normal text, `estimated` = hollow dashed
// dot + dashed connector + muted text, and the label itself names the state.
function TimelineRow({
  item,
  isLast,
  connectorDashed,
}: {
  item: TimelineItem;
  isLast: boolean;
  connectorDashed: boolean;
}) {
  const estimated = item.variant === 'estimated';
  const dotTint = item.dotColor === 'warning' ? 'warning.main' : 'text.primary';
  return (
    <Box component="li" sx={{ display: 'flex', gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          data-testid="timeline-marker"
          data-variant={item.variant}
          aria-hidden
          sx={{
            width: DOT_SIZE,
            height: DOT_SIZE,
            mt: 0.5,
            borderRadius: '50%',
            boxSizing: 'border-box',
            ...(estimated
              ? { border: '2px dashed', borderColor: 'text.primary', backgroundColor: 'transparent' }
              : { backgroundColor: dotTint }),
          }}
        />
        {!isLast && (
          <Box
            data-testid="timeline-connector"
            aria-hidden
            sx={{
              flexGrow: 1,
              mt: 0.5,
              borderLeft: connectorDashed ? '1px dashed' : '1px solid',
              borderColor: 'text.primary',
            }}
          />
        )}
      </Box>
      <Box sx={{ pb: isLast ? 0 : 2 }}>
        <Typography
          variant="body2"
          color={estimated ? 'text.secondary' : 'text.primary'}
          sx={{ fontWeight: 700 }}
        >
          {item.label}
        </Typography>
        {item.datetime !== undefined && (
          <Typography variant="caption" color="text.secondary" component="div">
            {item.datetime}
          </Typography>
        )}
        {item.badge !== undefined && (
          <Typography variant="caption" color="primary.main" component="div">
            {item.badge}
          </Typography>
        )}
        {item.note !== undefined && (
          <Box data-testid="timeline-note" sx={{ mt: 0.5 }}>
            <Chip label={item.note} bold labelSize="small" />
          </Box>
        )}
        {item.action !== undefined && (
          <Box data-testid="timeline-action" sx={{ mt: 1 }}>
            {item.action}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Vertical timeline of state-change events as a semantic list. Ordering and composition
// are the caller's job (this renders only what it is handed); an empty list renders an
// empty <ul> and the caller shows its own "no history" copy.
export function Timeline({ items }: TimelineProps) {
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {items.map((item, index) => (
        <TimelineRow
          key={`${item.variant}-${item.label}-${item.datetime ?? ''}`}
          item={item}
          isLast={index === items.length - 1}
          connectorDashed={items[index + 1]?.variant === 'estimated'}
        />
      ))}
    </Box>
  );
}
